import { ResultCode } from '@adyen/adyen-web/dist/types/components/types'

import useCurrentUser from '~/hooks/useCurrentUser'
import { AdyenRedirectStatus, Coupon, StripePrice, StripeSubscription } from '~/subscription/types/subscription.types'

import { AppLanguage } from '~/types/locale'

import { THREE_DECIMAL_CURRENCIES, ZERO_DECIMAL_CURRENCIES } from './const'

export function useIsProfileCompleteForSubscription(): boolean {
  const user = useCurrentUser()
  return Boolean(user && (user.email || user.facebookEmail || user.authEmail))
}

export const scrollToSection = (sectionId: string): void => {
  const section = document.getElementById(sectionId)
  if (section) section.scrollIntoView({ behavior: 'smooth' })
}

export const getTrialPeriodInDaysForUser = (currPrice: StripePrice, currSubscription?: StripeSubscription): number => {
  const currSubscriptionData = currSubscription?.items?.data[0]

  if (!currPrice || Object.keys(currPrice).length === 0) return 0
  if (!currSubscriptionData || currPrice.product.id === currSubscriptionData?.price.product.id) {
    return currPrice.recurring?.trial_period_days || 0
  }
  const currSubscriptionCurrency = currSubscription.currency
  const currPriceUnitAmount = currPrice.currency_options[currSubscriptionCurrency]?.unit_amount ?? 0
  const currSubscriptionPriceUnitAmount =
    currSubscriptionData?.price.currency_options[currSubscriptionCurrency].unit_amount
  if (currSubscriptionPriceUnitAmount < currPriceUnitAmount) {
    return currPrice.recurring?.trial_period_days ?? 0
  }
  return 0
}

export const setDefaultCurrencyOfPriceToSubscriptionCurrency = (
  currPrice?: StripePrice,
  currSubscription?: StripeSubscription,
): StripePrice | undefined => {
  if (!currPrice || !currSubscription) return currPrice ? currPrice : undefined
  const currSubscriptionCurrency = currSubscription?.currency
  const currPriceSubscriptionCurrencyOption = currPrice?.currency_options[currSubscriptionCurrency]
    ? { ...currPrice?.currency_options[currSubscriptionCurrency], currency: currSubscriptionCurrency }
    : {}
  return { ...currPrice, ...currPriceSubscriptionCurrencyOption }
}

export function getAdyenRedirectStatus(resultCode: ResultCode): string {
  switch (resultCode) {
    case 'Authorised':
      return AdyenRedirectStatus.SUCCEEDED
    case 'Pending':
    case 'Received':
      return AdyenRedirectStatus.PENDING
    case 'Error':
      return AdyenRedirectStatus.ERROR
    case 'Cancelled':
      return AdyenRedirectStatus.CANCELLED
    default:
      return AdyenRedirectStatus.REFUSED
  }
}

export function getAdyenErrorStatus(errorName: string): string {
  switch (errorName) {
    case 'CANCEL':
      return AdyenRedirectStatus.CANCELLED
    case 'NETWORK_ERROR':
    default:
      return AdyenRedirectStatus.ERROR
  }
}

export function transformPrice(amount: number, currency: string): number {
  if (ZERO_DECIMAL_CURRENCIES.includes(currency)) {
    return amount
  } else if (THREE_DECIMAL_CURRENCIES.includes(currency)) {
    return Number((Math.round(amount) / 1000).toFixed(2))
  } else {
    return Number((Math.round(amount) / 100).toFixed(2))
  }
}

// Shared GA4 ecommerce items[] entry for subscription funnel events (select_item / begin_checkout
// / add_payment_info). Mirrors the item shape produced for the subscription purchase event so the
// whole funnel reports on a consistent item.
export function buildSubscriptionEcommerceItem(priceDetails?: {
  product?: { id?: string; name?: string }
  unit_amount?: number
  currency?: string
  recurring?: { interval?: string } | null
}): Record<string, unknown> {
  return {
    item_id: priceDetails?.product?.id,
    item_name: priceDetails?.product?.name,
    price: transformPrice(priceDetails?.unit_amount ?? 0, priceDetails?.currency ?? ''),
    currency: priceDetails?.currency,
    item_category: 'subscription',
    recurring_interval: priceDetails?.recurring?.interval,
    quantity: 1,
  }
}

export function calculateCouponDiscount(coupon: Coupon | undefined, total: number): number {
  if (!coupon) return 0

  if (coupon.percent_off) {
    return (total * coupon.percent_off) / 100
  }

  return coupon.amount_off || 0
}

const languageLocaleMap: Record<string, string> = {
  [AppLanguage.EN]: 'en-US',
  [AppLanguage.NO]: 'nb-NO',
  [AppLanguage.UA]: 'uk-UA',
}

/**
 * Format a price to a local currency
 * @param currency - The currency code
 * @param price - The price to format
 * @param language - The app language; drives decimal and grouping separators
 * @returns The formatted price
 * @example formatToLocalCurrency('USD', 1000, AppLanguage.EN) // $1,000.00
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
 */
export const formatToLocalCurrency = (
  currency: string,
  price: number,
  language?: AppLanguage,
): { priceLabel: string; currencyIcon: string; numberPriceLabel: string } => {
  if (!currency) {
    return { priceLabel: price.toString(), currencyIcon: '', numberPriceLabel: price.toString() }
  }
  const currencyLowerCase = currency.toLowerCase()
  const locale = languageLocaleMap[language ?? AppLanguage.EN] ?? 'en-US'

  const currencyFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyLowerCase,
  })
  const priceParts = currencyFormatter.formatToParts(price)
  const currencyIcon = priceParts.find((part) => part.type === 'currency')?.value || ''

  const numberPriceLabel = filterPriceParts(priceParts, false)
  const priceLabel = filterPriceParts(priceParts, true)
  return { numberPriceLabel, currencyIcon, priceLabel }
}

function filterPriceParts(priceParts: Intl.NumberFormatPart[], keepCurrency: boolean): string {
  const hasZeroFraction = priceParts.some((part) => part.type === 'fraction' && Number(part.value) === 0)
  return priceParts
    .filter(
      (part) =>
        (keepCurrency || part.type !== 'currency') &&
        !((part.type === 'fraction' || part.type === 'decimal') && hasZeroFraction),
    )
    .map((part) => part.value)
    .join('')
    .trim()
}

export const getCouponValidityText = (
  couponDetails: Coupon | undefined,
  stripePaymentInterval: string,
  t: any,
): string => {
  if (!couponDetails) return ''

  if (couponDetails.duration === 'forever') {
    return t('couponForever')
  }

  if (couponDetails.duration === 'once') {
    return t('couponOnce')
  }

  if (couponDetails.duration === 'repeating' && couponDetails.duration_in_months) {
    const months = couponDetails.duration_in_months
    if (stripePaymentInterval === 'year') {
      const years = Math.floor(months / 12)
      return t('couponValidForYears', { years: months >= 12 ? years : 1 })
    }

    const years = Math.floor(months / 12)
    const monthsRemainder = months % 12

    if (years > 0 && monthsRemainder > 0) {
      return t('couponValidForYearsAndMonths', { years, months: monthsRemainder })
    }

    return t('couponValidForMonths', { months })
  }

  return t('couponForever')
}
